/**
 * Layout Algorithms for Flowchart Rearrangement
 * Implements various graph layout algorithms for organizing nodes
 */

/**
 * Hierarchical (Sugiyama-style) layout with parent-child centering.
 *
 * Algorithm:
 * 1. BFS from root nodes to assign levels (longest-path for proper depth).
 * 2. Barycenter ordering — sort nodes within each level by mean parent position
 *    to minimise edge crossings.
 * 3. Two-pass coordinate assignment:
 *    a) Top-down: place children centred under their parents.
 *    b) Bottom-up: shift parents to centre over their children.
 * 4. Compact by removing unused horizontal space.
 */
export const hierarchical=(nodes,edges,direction='TB') => {
    if(!nodes||nodes.length===0) return [];

    const hGap=200;   // horizontal distance between node centres
    const vGap=120;   // vertical distance between levels

    // --- adjacency ----------------------------------------------------------
    const childrenMap=new Map(nodes.map(n => [n.id,[]]));
    const parentsMap=new Map(nodes.map(n => [n.id,[]]));
    const inDeg=new Map(nodes.map(n => [n.id,0]));

    edges.forEach(e => {
        if(childrenMap.has(e.source)) childrenMap.get(e.source).push(e.target);
        if(parentsMap.has(e.target)) parentsMap.get(e.target).push(e.source);
        inDeg.set(e.target,(inDeg.get(e.target)||0)+1);
    });

    // --- 1. Level assignment (BFS, longest path) ----------------------------
    const level=new Map();
    const roots=nodes.filter(n => (inDeg.get(n.id)||0)===0).map(n => n.id);
    if(roots.length===0) {
        const minD=Math.min(...Array.from(inDeg.values()));
        const fallback=Array.from(inDeg.entries()).find(([,d]) => d===minD);
        if(fallback) roots.push(fallback[0]);
    }

    const queue=[...roots];
    roots.forEach(id => level.set(id,0));
    const visited=new Set();
    let maxIter=nodes.length*nodes.length+nodes.length;
    while(queue.length>0&&maxIter-->0) {
        const id=queue.shift();
        if(visited.has(id)) continue;
        const pars=parentsMap.get(id)||[];
        if(pars.length>0&&!pars.every(p => level.has(p))) {
            queue.push(id);
            continue;
        }
        visited.add(id);
        const myLevel=pars.length>0
            ? Math.max(...pars.map(p => level.get(p)??0))+1
            :(level.get(id)??0);
        level.set(id,myLevel);
        (childrenMap.get(id)||[]).forEach(c => {
            if(!visited.has(c)) {
                level.set(c,Math.max(level.get(c)??0,myLevel+1));
                queue.push(c);
            }
        });
    }
    nodes.forEach(n => {if(!level.has(n.id)) level.set(n.id,0);});

    // Group by level
    const levels=new Map();
    nodes.forEach(n => {
        const l=level.get(n.id);
        if(!levels.has(l)) levels.set(l,[]);
        levels.get(l).push(n.id);
    });
    const sortedLevels=Array.from(levels.keys()).sort((a,b) => a-b);

    // --- 2. Barycenter ordering (reduce crossings) --------------------------
    const slot=new Map();
    sortedLevels.forEach((lv,li) => {
        const ids=levels.get(lv);
        if(li>0) {
            ids.sort((a,b) => {
                const pa=parentsMap.get(a)||[];
                const pb=parentsMap.get(b)||[];
                const avgA=pa.length>0? pa.reduce((s,p) => s+(slot.get(p)??0),0)/pa.length:0;
                const avgB=pb.length>0? pb.reduce((s,p) => s+(slot.get(p)??0),0)/pb.length:0;
                return avgA-avgB;
            });
        }
        ids.forEach((id,i) => slot.set(id,i));
    });

    // --- 3. Coordinate assignment (two-pass) --------------------------------
    const pos=new Map();

    // Pass A — top-down: centre children under parent midpoint
    sortedLevels.forEach((lv) => {
        const ids=levels.get(lv);
        ids.forEach((id,i) => {
            const pars=parentsMap.get(id)||[];
            if(pars.length>0&&pars.every(p => pos.has(p))) {
                const avg=pars.reduce((s,p) => s+pos.get(p),0)/pars.length;
                pos.set(id,avg);
            } else {
                pos.set(id,i*hGap);
            }
        });
        // Resolve overlaps
        const sorted=[...ids].sort((a,b) => pos.get(a)-pos.get(b));
        for(let i=1;i<sorted.length;i++) {
            const prev=pos.get(sorted[i-1]);
            const cur=pos.get(sorted[i]);
            if(cur-prev<hGap) pos.set(sorted[i],prev+hGap);
        }
    });

    // Pass B — bottom-up: shift parents to centre over their children
    for(let li=sortedLevels.length-1;li>=0;li--) {
        const lv=sortedLevels[li];
        const ids=levels.get(lv);
        ids.forEach(id => {
            const ch=(childrenMap.get(id)||[]).filter(c => pos.has(c));
            if(ch.length>0) {
                const childMid=ch.reduce((s,c) => s+pos.get(c),0)/ch.length;
                pos.set(id,childMid);
            }
        });
        const sorted=[...ids].sort((a,b) => pos.get(a)-pos.get(b));
        for(let i=1;i<sorted.length;i++) {
            const prev=pos.get(sorted[i-1]);
            const cur=pos.get(sorted[i]);
            if(cur-prev<hGap) pos.set(sorted[i],prev+hGap);
        }
    }

    // Centre the whole graph around x=0
    const allPos=Array.from(pos.values());
    const minP=Math.min(...allPos);
    const maxP=Math.max(...allPos);
    const offset=(minP+maxP)/2;
    pos.forEach((v,k) => pos.set(k,v-offset));

    // --- 4. Build final positions -------------------------------------------
    const newPositions=[];
    nodes.forEach(n => {
        const primary=pos.get(n.id)??0;
        const lv=level.get(n.id)??0;
        const secondary=lv*vGap;
        let x,y;
        if(direction==='TB') {x=primary; y=secondary;}
        else if(direction==='BT') {x=primary; y=-secondary;}
        else if(direction==='LR') {x=secondary; y=primary;}
        else {x=-secondary; y=primary;}
        newPositions.push({id: n.id,position: {x,y}});
    });

    return newPositions;
};

/**
 * Calculate grid layout
 * Arranges nodes in a grid pattern
 */
export const grid=(nodes,edges,columns='auto') => {
    if(!nodes||nodes.length===0) return [];

    const cols=columns==='auto'? Math.ceil(Math.sqrt(nodes.length)):columns;
    const horizontalGap=250;
    const verticalGap=150;

    return nodes.map((node,index) => {
        const row=Math.floor(index/cols);
        const col=index%cols;

        return {
            id: node.id,
            position: {
                x: col*horizontalGap,
                y: row*verticalGap
            }
        };
    });
};

/**
 * Calculate radial layout
 * Arranges nodes in concentric circles
 */
export const radial=(nodes,edges,centerNodeId=null) => {
    if(!nodes||nodes.length===0) return [];

    // Find center node
    let centerId=centerNodeId;
    if(!centerId) {
        // Use node with most connections
        const connections=new Map(nodes.map(n => [n.id,0]));
        edges.forEach(edge => {
            connections.set(edge.source,(connections.get(edge.source)||0)+1);
            connections.set(edge.target,(connections.get(edge.target)||0)+1);
        });
        const maxConnections=Math.max(...Array.from(connections.values()));
        centerId=Array.from(connections.entries())
            .find(([_,count]) => count===maxConnections)?.[0]||nodes[0].id;
    }

    // BFS to assign nodes to rings
    const nodeMap=new Map(nodes.map(n => [n.id,{...n,ring: -1}]));
    const adjacency=new Map(nodes.map(n => [n.id,[]]));

    edges.forEach(edge => {
        adjacency.get(edge.source)?.push(edge.target);
        adjacency.get(edge.target)?.push(edge.source);
    });

    const queue=[{id: centerId,ring: 0}];
    const visited=new Set();

    while(queue.length>0) {
        const {id,ring}=queue.shift();
        if(visited.has(id)) continue;
        visited.add(id);

        const node=nodeMap.get(id);
        if(node) node.ring=ring;

        const neighbors=adjacency.get(id)||[];
        neighbors.forEach(neighborId => {
            if(!visited.has(neighborId)) {
                queue.push({id: neighborId,ring: ring+1});
            }
        });
    }

    // Assign ring 0 to unvisited nodes
    nodeMap.forEach(node => {
        if(node.ring===-1) node.ring=0;
    });

    // Group by ring
    const rings=new Map();
    nodeMap.forEach(node => {
        if(!rings.has(node.ring)) {
            rings.set(node.ring,[]);
        }
        rings.get(node.ring).push(node);
    });

    // Position nodes in rings
    const newPositions=[];
    const ringRadius=200;

    rings.forEach((ringNodes,ringIndex) => {
        if(ringIndex===0) {
            // Center node
            newPositions.push({
                id: ringNodes[0].id,
                position: {x: 0,y: 0}
            });
        } else {
            const radius=ringIndex*ringRadius;
            const angleStep=(2*Math.PI)/ringNodes.length;

            ringNodes.forEach((node,index) => {
                const angle=index*angleStep;
                newPositions.push({
                    id: node.id,
                    position: {
                        x: radius*Math.cos(angle),
                        y: radius*Math.sin(angle)
                    }
                });
            });
        }
    });

    return newPositions;
};

/**
 * Calculate force-directed layout
 * Uses simple force simulation (Fruchterman-Reingold inspired)
 */
export const forceDirected=(nodes,edges,iterations=100) => {
    if(!nodes||nodes.length===0) return [];

    // Initialize positions randomly if not set
    const positions=nodes.map(node => ({
        id: node.id,
        x: node.position?.x??(Math.random()-0.5)*500,
        y: node.position?.y??(Math.random()-0.5)*500,
        vx: 0,
        vy: 0
    }));

    const k=Math.sqrt((1000*1000)/nodes.length); // Optimal distance
    const temperature=100;

    for(let iter=0;iter<iterations;iter++) {
        const temp=temperature*(1-iter/iterations);

        // Repulsive forces between all pairs
        for(let i=0;i<positions.length;i++) {
            positions[i].vx=0;
            positions[i].vy=0;

            for(let j=0;j<positions.length;j++) {
                if(i===j) continue;

                const dx=positions[i].x-positions[j].x;
                const dy=positions[i].y-positions[j].y;
                const dist=Math.sqrt(dx*dx+dy*dy)||1;

                const force=(k*k)/dist;
                positions[i].vx+=(dx/dist)*force;
                positions[i].vy+=(dy/dist)*force;
            }
        }

        // Attractive forces along edges
        edges.forEach(edge => {
            const source=positions.find(p => p.id===edge.source);
            const target=positions.find(p => p.id===edge.target);

            if(source&&target) {
                const dx=target.x-source.x;
                const dy=target.y-source.y;
                const dist=Math.sqrt(dx*dx+dy*dy)||1;

                const force=(dist*dist)/k;
                const fx=(dx/dist)*force;
                const fy=(dy/dist)*force;

                source.vx+=fx;
                source.vy+=fy;
                target.vx-=fx;
                target.vy-=fy;
            }
        });

        // Update positions
        positions.forEach(pos => {
            const dist=Math.sqrt(pos.vx*pos.vx+pos.vy*pos.vy)||1;
            const movement=Math.min(dist,temp);

            pos.x+=(pos.vx/dist)*movement;
            pos.y+=(pos.vy/dist)*movement;
        });
    }

    return positions.map(pos => ({
        id: pos.id,
        position: {x: Math.round(pos.x),y: Math.round(pos.y)}
    }));
};

/**
 * Calculate tree layout
 * Specialized hierarchical layout for tree structures
 */
export const tree=(nodes,edges,rootNodeId=null) => {
    if(!nodes||nodes.length===0) return [];

    // Find root node (node with no incoming edges)
    const inDegree=new Map(nodes.map(n => [n.id,0]));
    edges.forEach(edge => {
        inDegree.set(edge.target,(inDegree.get(edge.target)||0)+1);
    });

    let rootId=rootNodeId;
    if(!rootId) {
        rootId=Array.from(inDegree.entries())
            .find(([_,degree]) => degree===0)?.[0]||nodes[0].id;
    }

    // Build tree structure
    const children=new Map(nodes.map(n => [n.id,[]]));
    edges.forEach(edge => {
        children.get(edge.source)?.push(edge.target);
    });

    // Calculate subtree sizes
    const subtreeSizes=new Map();
    const calculateSize=(nodeId) => {
        const childIds=children.get(nodeId)||[];
        if(childIds.length===0) {
            subtreeSizes.set(nodeId,1);
            return 1;
        }
        const size=childIds.reduce((sum,childId) => sum+calculateSize(childId),1);
        subtreeSizes.set(nodeId,size);
        return size;
    };
    calculateSize(rootId);

    // Position nodes
    const horizontalGap=200;
    const verticalGap=150;
    const newPositions=[];

    const positionNode=(nodeId,x,y,availableWidth) => {
        newPositions.push({
            id: nodeId,
            position: {x,y}
        });

        const childIds=children.get(nodeId)||[];
        if(childIds.length===0) return;

        const totalSize=subtreeSizes.get(nodeId)-1;
        let currentX=x-(totalSize*horizontalGap)/2;

        childIds.forEach(childId => {
            const childSize=subtreeSizes.get(childId);
            const childX=currentX+(childSize*horizontalGap)/2;
            positionNode(childId,childX,y+verticalGap,childSize*horizontalGap);
            currentX+=childSize*horizontalGap;
        });
    };

    positionNode(rootId,0,0,subtreeSizes.get(rootId)*horizontalGap);

    return newPositions;
};

/**
 * Main layout function - routes to specific algorithm
 */
export const calculateLayout=(nodes,edges,algorithm='hierarchical',options={}) => {
    const {direction='TB',columns='auto',centerNodeId=null,rootNodeId=null,iterations=100}=options;

    switch(algorithm) {
        case 'hierarchical':
            return hierarchical(nodes,edges,direction);
        case 'grid':
            return grid(nodes,edges,columns);
        case 'radial':
            return radial(nodes,edges,centerNodeId);
        case 'force-directed':
        case 'forceDirected':
            return forceDirected(nodes,edges,iterations);
        case 'tree':
            return tree(nodes,edges,rootNodeId);
        default:
            return hierarchical(nodes,edges,direction);
    }
};

export const layoutAlgorithms={
    hierarchical,
    grid,
    radial,
    forceDirected,
    tree,
    calculateLayout
};

export default layoutAlgorithms;
